import axios, { type AxiosAdapter, type InternalAxiosRequestConfig } from 'axios';
import {
  normalizeAdminMfaSetupResponse,
  normalizeAdminMfaStatus,
  normalizeAdminMfaVerifyResponse,
  type AdminMfaSetupResponse,
  type AdminMfaStatus,
  type AdminMfaVerifyResponse,
} from '../types/adminMfa';
import { getAdminAuthServerUrl } from './adminOAuth';
import { buildAdminMfaApiConfig } from './adminMfaConfig';
import { refreshAdminAccessToken, resolveAdminAccessToken } from './adminAccessToken';

type AdminMfaHttpOptions = {
  adapter?: AxiosAdapter;
  getAccessToken?: () => string | null;
  refreshAccessToken?: () => Promise<string | null>;
};

type RetriableMfaRequest = InternalAxiosRequestConfig & { adminMfaRetried?: boolean };

export const createAdminMfaHttp = ({
  adapter,
  getAccessToken = resolveAdminAccessToken,
  refreshAccessToken = refreshAdminAccessToken,
}: AdminMfaHttpOptions = {}) => {
  const http = axios.create({ ...buildAdminMfaApiConfig(getAdminAuthServerUrl()), adapter });
  http.interceptors.request.use((config: RetriableMfaRequest) => {
    if (!config.adminMfaRetried) {
      const token = getAccessToken();
      if (token) config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  });
  http.interceptors.response.use(undefined, async (error: unknown) => {
    if (!axios.isAxiosError(error)) throw error;
    const request = error.config as RetriableMfaRequest | undefined;
    const authorization = request?.headers.get('Authorization');
    // A rejected OTP is a completed verification attempt, not an expired bearer.
    const rejectedChallenge = request?.url === '/api/admin/mfa/verify'
      && error.response?.data?.verified === false;
    if (
      error.response?.status !== 401 || !request || request.adminMfaRetried || rejectedChallenge ||
      request.signal?.aborted || typeof authorization !== 'string' || !authorization.startsWith('Bearer ')
    ) {
      throw error;
    }

    request.adminMfaRetried = true;
    // A background refresh may have completed while this request was in flight.
    const currentToken = getAccessToken();
    const nextToken = currentToken && `Bearer ${currentToken}` !== authorization
      ? currentToken
      : await refreshAccessToken();
    if (request.signal?.aborted) throw new axios.CanceledError();
    if (!nextToken) throw error;

    request.headers.set('Authorization', `Bearer ${nextToken}`);
    return http.request(request);
  });
  return http;
};

const adminMfaHttp = createAdminMfaHttp();

export interface VerifyAdminMfaRequest {
  challengeId: string;
  code: string;
}

export const getAdminMfaStatus = async (signal?: AbortSignal): Promise<AdminMfaStatus> => {
  const { data } = await adminMfaHttp.request<unknown>({
    url: '/api/admin/mfa/status',
    method: 'get',
    signal,
  });

  return normalizeAdminMfaStatus(data);
};

export const beginAdminMfaSetup = async (signal?: AbortSignal): Promise<AdminMfaSetupResponse> => {
  const { data } = await adminMfaHttp.request<unknown>({
    url: '/api/admin/mfa/setup',
    method: 'post',
    signal,
  });

  return normalizeAdminMfaSetupResponse(data);
};

export const verifyAdminMfa = async (
  request: VerifyAdminMfaRequest,
  signal?: AbortSignal,
): Promise<AdminMfaVerifyResponse> => {
  const { data } = await adminMfaHttp.request<unknown>({
    url: '/api/admin/mfa/verify',
    method: 'post',
    data: request,
    signal,
  });

  return normalizeAdminMfaVerifyResponse(data);
};
